/**
 * callActions.js
 * Talkroute
 *
 * Created by Vel Genov on 3/7/19
 * Copyright (c) 2019 Talkroute. All right reserved.
 */
import { ErrorStatusCode } from "@helpers/hooks/useService.hook";
import { RootState } from "@helpers/store/store";
import { handleAuthorizationErrors } from "@helpers/thunkHelpers";
import { ThunkDispatch } from "redux-thunk";
import apiServiceInstance from "../services/TalkrouteAPIService";

/**
 * Constants for the different redux action types
 */
export const MAKE_CALL = "MAKE_CALL";
export const IDLE_CALL = "IDLE_CALL";
export const CALL_CONNECTED = "CALL_CONNECTED";
export const CALL_CONNECTING = "CALL_CONNECTING";
export const HANG_UP = "HANG_UP";
export const ACCEPT = "ACCEPT";
export const INCOMING_CALL = "INCOMING_CALL";
export const SET_OUTGOING_NUMBER = "SET_OUTGOING_NUMBER";
export const SET_NUMBER_TO_CALL = "SET_NUMBER_TO_CALL";
export const SET_IS_KEYPAD_VISIBLE = "SET_IS_KEYPAD_VISIBLE";
export const SET_IS_MUTE_MIC = "SET_IS_MUTE_MIC";
export const SET_IS_CALL_HOLD = "SET_IS_CALL_HOLD";
export const SET_DTMF_STRING = "SET_DTMF_STRING";
export const FETCH_BLOCKED_NUMBERS_SUCCESS = "FETCH_BLOCKED_NUMBERS_SUCCESS";
export const FETCH_BLOCKED_NUMBERS_FAILURE = "FETCH_BLOCKED_NUMBERS_FAILURE";
export const SET_BLOCKED_TYPE = "SET_BLOCKED_TYPE";
export const SET_LAST_CALLED_NUMBER = "SET_LAST_CALLED_NUMBER";
export const BLOCK_NUMBER_FAILURE = "BLOCK_NUMBER_FAILURE";
export const FETCH_BLOCKED_NUMBERS_PENDING = 'FETCH_BLOCKED_NUMBERS_PENDING';
export const BLOCK_NUMBER_STARTED = "BLOCK_NUMBER_STARTED";
export const BLOCK_NUMBER_SUCCESS = "BLOCK_NUMBER_SUCCESS";
export const UNBLOCK_NUMBER_FAILURE = "UNBLOCK_NUMBER_FAILURE";
export const UNBLOCK_NUMBER_STARTED = "UNBLOCK_NUMBER_STARTED";
export const UNBLOCK_NUMBER_SUCCESS = "UNBLOCK_NUMBER_SUCCESS";
export const SET_CALL_SESSION = "SET_CALL_SESSION";
export const OPEN_INCOMING_CALL_NOTIFICATION = "OPEN_INCOMING_CALL_NOTIFICATION";
export const CLOSE_INCOMING_CALL_NOTIFICATION = "CLOSE_INCOMING_CALL_NOTIFICATION";

export enum blockedTypes {
  NONE = "none",
  BLOCKED = "blocked",
  UNBLOCKED = "unblocked",
};

/**
 * Clear all call information
 */
export const idleCall = () => ({
  type: IDLE_CALL,
});

/**
 * Request to make a call to the passed in phone number
 * @param numberToCall the number to call
 */
export const makeCall = (numberToCall: string, outgoingPhoneNumber?: string) => ({
  type: MAKE_CALL,
  payload: { numberToCall, outgoingPhoneNumber },
});

/**
 * A call is currently connecting
 */
export const callConnecting = () => ({
  type: CALL_CONNECTING,
});

/**
 * A call has connected
 *
 * @param phoneNumber the phone number a call is connected to
 */
export const callConnected = (phoneNumber: string) => ({
  type: CALL_CONNECTED,
  payload: { phoneNumber },
});

/**
 * ACCEPT an existing incoming call
 */
export const accept = () => ({
  type: ACCEPT,
});

/**
 * Start a hangup action on the current call
 */
export const hangUp = () => ({
  type: HANG_UP,
});

/**
 * There is an incoming call
 *
 * @param fromNumber the number the call is coming from
 * @param dialedNumber the phone number that was dialed
 */
export const incomingCall = (fromNumber: string, dialedNumber: string) => ({
  type: INCOMING_CALL,
  payload: {
    fromNumber,
    dialedNumber,
  },
});

/**
 * Set the outgoing phone number to use
 *
 * @param outgoingPhoneNumber the outgoing phone number to use
 */
export const setOutgoingPhoneNumber = (outgoingPhoneNumber: string | null) => ({
  type: SET_OUTGOING_NUMBER,
  payload: {
    outgoingPhoneNumber,
  },
});

/**
 * Set the numberToCall to use
 *
 * @param numberToCall the numberToCall to use
 */
export const setNumberToCall = (numberToCall: string) => ({
  type: SET_NUMBER_TO_CALL,
  payload: {
    numberToCall,
  },
});

/**
 * Set the isKeypadVisible
 *
 * @param isKeypadVisible whether the keypad is visble or not during calling
 */
export const setIsKeypadVisible = (isKeypadVisible: boolean) => ({
  type: SET_IS_KEYPAD_VISIBLE,
  payload: {
    isKeypadVisible,
  },
});

/**
 * Set the isMuteMic
 *
 * @param isMuteMic whether the mic is mute or not
 */
export const setIsMuteMic = (isMuteMic: boolean) => ({
  type: SET_IS_MUTE_MIC,
  payload: {
    isMuteMic,
  },
});

/**
 * Set the isCallHold
 *
 * @param isCallHold whether the call is hold or not
 */
export const setIsCallHold = (isCallHold: boolean) => ({
  type: SET_IS_CALL_HOLD,
  payload: {
    isCallHold,
  },
});

/**
 * Set the dtmfString
 *
 * @param dtmfString The dtmfString user entered during call
 */
export const setDtmfString = (dtmfString: string) => ({
  type: SET_DTMF_STRING,
  payload: {
    dtmfString,
  },
});

/**
 * Gets the blocked phone numbers from a web service
 */
export const fetchBlockedNumbers = () => {
  return (dispatch: ThunkDispatch<any, any, any>, getState: () => RootState) => {
    const pending = getState().authentication.userPermissionsPending;
    // if (!pending) {
      dispatch(fetchBlockNumberPending(true));
      apiServiceInstance
        .getBlockedNumbers()
        .then((data: any) => {
          const blockedNumbers = data.reduce((acc, curNumber) => {
            if (curNumber.enabled) {
              return [...acc, curNumber.number];
            }
            return acc;
          }, []);
          dispatch(fetchBlockedNumbersSuccess(blockedNumbers));
          dispatch(fetchBlockNumberPending(false));
          return blockedNumbers;
        })
        .catch((error) => {
          if (error.originalError && error.originalError.isAxiosError) {
            error.originalError.response?.status === ErrorStatusCode.UNAUTHORIZED &&
            handleAuthorizationErrors(dispatch, getState().authentication)
          }
          dispatch(fetchBlockNumberPending(false));
          dispatch(fetchBlockedNumbersFailure(error.errorParsed));
        });
    // }
  }
};

/**
 * Block unwanted block number requests when pending
 */
 const fetchBlockNumberPending = (payload: boolean) => {
  return {                                                                                                   
    type: FETCH_BLOCKED_NUMBERS_PENDING,
    payload
  };
 }
/**
 * Block the call for the phone number
 *
 * @param {*} number the phone number which will be blocked
 */
export const blockNumber = (number) => {
  return (dispatch: ThunkDispatch<any, any, any>) => {
    dispatch(setBlockedType(blockedTypes.NONE));
    dispatch(blockNumberStarted());
    apiServiceInstance
      .blockNumber(number)
      .then((data) => {
        dispatch(fetchBlockedNumbers());
        dispatch(setBlockedType(blockedTypes.BLOCKED));
        dispatch(blockNumberSuccess());
        return data;
      })
      .catch((error) => {
        dispatch(blockNumberFailure(error));
      });
  };
};

/**
 * Unblock the call for the phone number
 *
 * @param {*} number the phone number which will be unblocked
 */
export const unblockNumber = (number) => {
  return (dispatch: ThunkDispatch<any, any, any>) => {
    dispatch(setBlockedType(blockedTypes.NONE));
    dispatch(unblockNumberStarted());
    apiServiceInstance
      .unblockNumber(number)
      .then((data) => {
        dispatch(fetchBlockedNumbers());
        dispatch(setBlockedType(blockedTypes.UNBLOCKED));
        dispatch(unblockNumberSuccess());
        return data;
      })
      .catch((error) => {
        dispatch(fetchBlockedNumbers());
        dispatch(unblockNumberFailure(error));
      });
  };
};

/**
 * Successfully fetched blocked numbers
 *
 * @param data The array of blocked numbers
 */
export const fetchBlockedNumbersSuccess = (data) => ({
  type: FETCH_BLOCKED_NUMBERS_SUCCESS,
  payload: { data },
});

/**
 * Failed to fetch blocked numbers
 *
 * @param error
 */
export const fetchBlockedNumbersFailure = (error) => ({
  type: FETCH_BLOCKED_NUMBERS_FAILURE,
  payload: { error },
});

/**
 *
 */
export const setBlockedType = (type) => ({
  type: SET_BLOCKED_TYPE,
  payload: { type },
});

export const setLastCalledNumber = (phoneNumber: string, outgoingPhoneNumber: string) => ({
  type: SET_LAST_CALLED_NUMBER,
  payload: { phoneNumber, outgoingPhoneNumber },
});

/**
 * Failed to block a number
 *
 * @param error
 */
export const blockNumberFailure = (error) => ({
  type: BLOCK_NUMBER_FAILURE,
  payload: error,
});

/**
 * Failed to unblock a number
 *
 * @param error
 */
export const unblockNumberFailure = (error) => ({
  type: UNBLOCK_NUMBER_FAILURE,
  payload: error,
});

/**
 * Success to unblock a number
 *
 * @param error
 */
export const unblockNumberSuccess = () => ({
  type: UNBLOCK_NUMBER_SUCCESS,
  payload: {},
});

/**
 * Success to block a number
 *
 * @param error
 */
export const blockNumberSuccess = () => ({
  type: BLOCK_NUMBER_SUCCESS,
  payload: {},
});

/**
 * Started to block a number
 *
 * @param error
 */
export const blockNumberStarted = () => ({
  type: BLOCK_NUMBER_STARTED,
  payload: {},
});

/**
 * Started to unblock a number
 *
 * @param error
 */
export const unblockNumberStarted = () => ({
  type: UNBLOCK_NUMBER_STARTED,
  payload: {},
});

/**
 * Set call session
 *
 * @param error
 */
export const setCallSession = (callSession) => ({
  type: SET_CALL_SESSION,
  payload: { callSession },
});

const callActions = {
  idleCall,
  makeCall,
  callConnecting,
  callConnected,
  accept,
  hangUp,
  incomingCall,
  setOutgoingPhoneNumber,
  setNumberToCall,
  setIsKeypadVisible,
  setIsMuteMic,
  setIsCallHold,
  setDtmfString,
  fetchBlockedNumbers,
  blockNumber,
  unblockNumber,
  fetchBlockedNumbersSuccess,
  fetchBlockedNumbersFailure,
  setBlockedType,
  setLastCalledNumber,
  setCallSession
};

export default callActions;
